import { useQuery } from '@tanstack/react-query';
import { useAuth, ROLE_RECRUITMENT_ADMIN, ROLE_SI_ADMIN, ROLE_SI_RH } from '@/contexts/AuthContext';
import { candidatoService } from '../../services/candidatoService';
import { mesclarCandidato, temCamposPrivadosNoAberto } from '../../models/candidato.privado';
import type { Candidato } from '../../models/candidato.types';

export const RECRUITMENT_CANDIDATOS_QUERY_KEYS = {
  all: ['recruitment_candidatos'] as const,
};

/** Quem vê e-mail, status completo e respostas do candidato (o registro protegido). */
export function usePodeVerDadosCandidato(): boolean {
  const { userHasRole } = useAuth();
  return userHasRole(ROLE_RECRUITMENT_ADMIN) || userHasRole(ROLE_SI_ADMIN) || userHasRole(ROLE_SI_RH);
}

/**
 * Lista de candidatos já mesclada (aberto + protegido, ver models/candidato.privado.ts) — as
 * telas recebem o mesmo `Candidato` de antes. Só busca o protegido quem pode ver (a regra
 * recusaria os demais). Quando quem abre tem Recrutamento e ainda há candidato no formato
 * antigo (tudo no aberto), migra na hora e segue.
 *
 * `enabled: !!user` evita disparar a leitura antes do Firebase Auth terminar de restaurar a
 * sessão (mesmo padrão de useProposalsQuery).
 */
export function useCandidatosQuery() {
  const { user, userHasRole } = useAuth();
  const podeVerPrivado = usePodeVerDadosCandidato();
  const isRecrutamento = userHasRole(ROLE_RECRUITMENT_ADMIN);

  return useQuery({
    queryKey: [...RECRUITMENT_CANDIDATOS_QUERY_KEYS.all, podeVerPrivado ? 'com-dados' : 'so-nomes'],
    queryFn: async (): Promise<Candidato[]> => {
      let abertos = await candidatoService.listar();
      let privados = podeVerPrivado ? await candidatoService.listarPrivados() : new Map<string, Record<string, unknown>>();

      if (isRecrutamento && abertos.some((c) => temCamposPrivadosNoAberto(c.dados))) {
        try {
          await candidatoService.migrarCamposPrivados(abertos, privados);
          abertos = await candidatoService.listar();
          privados = await candidatoService.listarPrivados();
        } catch (error) {
          // Não bloqueia a lista — tenta de novo na próxima abertura; mesclarCandidato ainda lê o formato antigo.
          console.error('Não foi possível separar os dados protegidos dos candidatos antigos:', error);
        }
      }

      return abertos.map((c) => mesclarCandidato(c.id, c.dados, privados.get(c.id), podeVerPrivado));
    },
    enabled: !!user,
  });
}
